import { useCallback, useContext, type FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { asMoney } from '@trello/legacy-i18n/formatters';
import { Button } from '@trello/nachos/button';
import type { BillingIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { PlanComparisonContext } from '../PlanComparisonContext';
import type { BillingQuotes } from '../types';

import styles from './PlanComparisonStandardHeaderCell.less';

export interface PlanComparisonStandardHeaderCellProps {
  teamId: string;
  teamName: string;
  isMonthly: boolean;
  prices?: BillingQuotes['standardPrices'];
  isInPPCohort?: boolean;
}

export const PlanComparisonStandardHeaderCell: FunctionComponent<
  PlanComparisonStandardHeaderCellProps
> = ({ teamId, isMonthly, prices, isInPPCohort }) => {
  const intl = useIntl();
  const { variation, onChooseStandard } = useContext(PlanComparisonContext);

  const productCode = isMonthly
    ? prices?.monthly.productCode
    : prices?.annual.productCode;
  const pricePerUser = isMonthly
    ? prices?.monthly.subTotalPerUser
    : prices?.annual.subTotalPerUser;
  const frequency = isMonthly ? (
    <FormattedMessage
      id="templates.billing_page_one.per-month"
      defaultMessage="per month"
      description="per month"
    />
  ) : (
    <FormattedMessage
      id="templates.billing_page_one.per-year"
      defaultMessage="per year"
      description="per year"
    />
  );

  const onClick = useCallback(() => {
    if (variation === 'end-of-trial-friction-overlay') {
      Analytics.sendClickedButtonEvent({
        buttonName: 'chooseStandardButton',
        source: 'trelloPlanPricingModal',
        attributes: {
          viaEndOfTrial: true,
        },
      });
    }

    if (variation === 'billing-page') {
      Analytics.sendClickedButtonEvent({
        buttonName: 'upgradeToStandardButton',
        source: 'planComparisonSection',
        containers: {
          organization: {
            id: teamId,
          },
        },
      });
    }

    onChooseStandard?.(productCode!);
  }, [productCode, onChooseStandard, teamId, variation]);

  return (
    <th
      className={styles.planCell}
      data-value={intl.formatMessage({
        id: 'templates.billing_page_one.best-value',
        defaultMessage: 'BEST VALUE',
        description: 'BEST VALUE',
      })}
      data-testid={getTestId<BillingIds>('plan-comparison-cell-standard')}
    >
      <h3 className={styles.name}>
        <FormattedMessage
          id="templates.billing_page_one.standard-name"
          defaultMessage="Standard"
          description="Standard"
        />
      </h3>
      <p className={styles.info}>
        {isInPPCohort ? (
          <FormattedMessage
            id="templates.billing_page_one.standard-description-personal-productivity"
            // eslint-disable-next-line formatjs/no-multiple-whitespaces
            defaultMessage="Get more done with advanced Planner, card mirroring,  collapsable lists, list colors, and more."
            description="Get more done with advanced Planner, card mirroring, collapsable lists, list colors, and more."
          />
        ) : (
          <FormattedMessage
            id="templates.billing_page_one.standard-description-repackaging-gtm"
            defaultMessage="Take your team to the next level of productivity."
            description="Take your team to the next level of productivity."
          />
        )}
      </p>
      <span className={styles.price}>
        {intl
          .formatMessage(
            {
              id: 'templates.billing_page_one.amount-usd',
              defaultMessage: '${amount} USD',
              description: 'amount-usd',
            },
            {
              amount: asMoney(pricePerUser || 0).replace(/\.00$/, ''),
            },
          )
          .replace(/\s/g, ' ')}
      </span>
      <p className={styles.frequency}>
        <FormattedMessage
          id="templates.billing_page_one.per-user"
          defaultMessage="per user"
          description="per user"
        />
        <br />
        {frequency}
      </p>
      <Button
        key={
          variation === 'billing-page' ? `upgrade-standard` : `modal-standard`
        }
        onClick={onClick}
        testId={
          variation === 'billing-page'
            ? getTestId<BillingIds>('upgrade-team-standard-button')
            : getTestId<BillingIds>('get-standard')
        }
      >
        {variation === 'reverse-trial-overlay' ||
        variation === 'billing-page' ? (
          <FormattedMessage
            id="templates.billing_page_one.upgrade-now"
            defaultMessage="Upgrade now"
            description="Upgrade now"
          />
        ) : (
          <FormattedMessage
            id="templates.billing_page_one.get-standard"
            defaultMessage="Get Standard"
            description="Get Standard"
          />
        )}
      </Button>
    </th>
  );
};
